import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, FileText, Handshake, LineChart, Sparkles } from "lucide-react";
import OnboardingModal from "@/components/OnboardingModal";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/projects"),
      api.get("/sponsors/all"),
      api.get("/analytics/summary"),
    ]).then(([p, s, a]) => {
      setProjects(p.data);
      setSponsors(s.data);
      setAnalytics(a.data);
    }).catch(() => {});
  }, []);

  const now = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const signedRevenue = sponsors.filter(s => ["signed", "live", "paid"].includes(s.stage)).reduce((n, s) => n + (s.payout || 0), 0);
  const openDeals = sponsors.filter(s => ["pitched", "negotiating"].includes(s.stage)).length;

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto sd-rise">
      <OnboardingModal />
      <div className="flex items-baseline justify-between mb-2">
        <div className="sd-label">{now.toUpperCase()}</div>
        <div className="sd-label">V3.0</div>
      </div>
      <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="dashboard-heading">
        Welcome back, <span className="italic text-zinc-400">{user?.name?.split(" ")[0] || "creator"}</span>.
      </h1>
      <p className="mt-6 text-zinc-400 max-w-2xl">
        Here's your workspace pulse — projects in flight, sponsors on the board, and how last week performed.
      </p>

      {/* KPI row */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "PROJECTS", value: projects.length, hint: "active" },
          { label: "SIGNED REV.", value: `$${signedRevenue.toLocaleString()}`, hint: "committed" },
          { label: "OPEN DEALS", value: openDeals, hint: "pipeline" },
          { label: "30D VIEWS", value: analytics?.kpis?.youtube?.views_30d?.toLocaleString() || "—", hint: "youtube" },
        ].map((k) => (
          <div key={k.label} className="sd-card p-6">
            <div className="sd-label">{k.label}</div>
            <div className="font-mono text-3xl mt-3">{k.value}</div>
            <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>
          </div>
        ))}
      </div>

      {/* Quick modules */}
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        {[
          { to: "/editor", icon: FileText, tag: "SCRIPTS", title: "Continue writing", body: "Pick up where you left off in the script editor." },
          { to: "/sponsors", icon: Handshake, tag: "SPONSORS", title: "Move the pipeline", body: "Drag deals across stages. Parse a new contract PDF." },
          { to: "/analytics", icon: LineChart, tag: "ANALYTICS", title: "Check the numbers", body: "YouTube and Spotify metrics for the last 30 days." },
          { to: "/repurpose", icon: Sparkles, tag: "REPURPOSE", title: "Turn a script into a week", body: "Threads, hooks, newsletter — all from one paste." },
        ].map((m) => (
          <Link
            key={m.to}
            to={m.to}
            data-testid={`dashboard-cta-${m.tag.toLowerCase()}`}
            className="sd-card p-8 group hover:border-zinc-500 transition-colors"
          >
            <div className="flex items-start justify-between mb-6">
              <m.icon size={22} strokeWidth={1.25} className="text-[#FF3B30]" />
              <ArrowUpRight size={18} strokeWidth={1.25} className="text-zinc-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </div>
            <div className="sd-label mb-2">{m.tag}</div>
            <div className="font-display text-2xl">{m.title}</div>
            <div className="text-sm text-zinc-500 mt-2">{m.body}</div>
          </Link>
        ))}
      </div>

      {/* Recent projects */}
      <div className="mt-14">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-3xl">Recent projects</h2>
          <div className="sd-label">{projects.length} TOTAL</div>
        </div>
        <div className="border-t border-[#27272a]">
          {projects.slice(0, 5).map((p) => (
            <div key={p.project_id} className="grid grid-cols-[8px_1fr_120px_140px] gap-4 items-center py-4 border-b border-[#27272a]">
              <span className="w-2 h-2" style={{ background: p.cover_color || "#FF3B30" }} />
              <div>
                <div className="text-white">{p.title}</div>
                <div className="text-xs text-zinc-500">{p.description || "—"}</div>
              </div>
              <div className="sd-label">{p.kind}</div>
              <div className="font-mono text-xs text-zinc-500 text-right">
                {new Date(p.updated_at).toLocaleDateString()}
              </div>
            </div>
          ))}
          {projects.length === 0 && (
            <div className="py-8 text-sm text-zinc-500">No projects yet. Create one from the sidebar.</div>
          )}
        </div>
      </div>
    </div>
  );
}
